import {
  FormControl,
  Grid,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { getCompanyConfig } from 'lib/api/company';
import { getDueWorkingDay } from 'lib/functions/dates';
import { formatMoney } from 'lib/functions/utils';
import DocumentsSuggestedContainer from 'pages/Finance/Components/Financing/common/documentSugestion';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { calculateDateToAdd } from '../../helpers/common/calculatePaymentDates';
import GuaranteeObservationPopper from './GuaranteePopper';

const Params = (props: any) => {
  const {
    creditDetails,
    setCreditDetails,
    editing,
    workingDayActive,
    setWorkingDayActive,
    selectedRequest,
    setDocumentsToApply,
    selectedDocuments,
    setSelectedDocuments,
    executivesPortfolio,
    executivesAll,
  } = props;

  const dispatch = useDispatch();
  const handleChangeMoney = (value: string, key: string) => {
    const newValue = value.split('$').join('').split('.').join('');
    setCreditDetails({ ...creditDetails, [key]: Number(newValue) });
  };
  const [configurations, setConfigurations] = useState({
    rateZero: false,
    arrearRateZero: false,
    subproductsAvailable: '',
    interestCalculationAvailable: '',
  });
  const [documents, setDocuments] = useState<any>([]);
  const maxAmount = creditDetails.amount || 0;
  const operationDate =
    selectedRequest?.requestDate || new Date().toISOString();

  const handleAmountToApply = (amount: number) => {
    setCreditDetails((prev: any) => ({
      ...prev,
      amountToApply: amount,
    }));
  };

  // Parse configuration values to get available options
  const parseConfigOptions = (configValue: string) => {
    if (!configValue) return [];
    try {
      // Try to parse as JSON first
      return JSON.parse(configValue);
    } catch {
      // If not JSON, try comma-separated values
      return configValue
        .split(',')
        .map((item) => item.trim())
        .filter((item) => item);
    }
  };

  const availableSubproducts = parseConfigOptions(
    configurations.subproductsAvailable,
  );
  const availableInterestCalculations = parseConfigOptions(
    configurations.interestCalculationAvailable,
  );

  useEffect(() => {
    setConfigurationsValues();
  }, []);

  useEffect(() => {
    if (creditDetails.documentsToApply && setSelectedDocuments) {
      setSelectedDocuments(
        creditDetails.documentsToApply.map((doc: any) => doc.id) || [],
      );
    }
  }, []);

  // Force re-render when executives are loaded
  useEffect(() => {
    // This effect will trigger a re-render when executives are loaded
  }, [executivesPortfolio, executivesAll]);

  const setConfigurationsValues = async () => {
    try {
      const configurationsResponse = await getCompanyConfig(
        dispatch,
        [
          'credit_request_rate_zero',
          'credit_request_arrear_rate_zero',
          'credit_request_subproducts_available',
          'credit_request_interest_calculation_availables',
        ].join(','),
      );

      //console.log("configurationsResponse -> ", configurationsResponse);

      const rateZero = configurationsResponse.credit_request_rate_zero === '1';
      const arrearRateZero =
        configurationsResponse.credit_request_arrear_rate_zero === '1';
      const subproductsAvailable =
        configurationsResponse.credit_request_subproducts_available;
      const interestCalculationAvailable =
        configurationsResponse.credit_request_interest_calculation_availables;

      setConfigurations({
        rateZero,
        arrearRateZero,
        subproductsAvailable,
        interestCalculationAvailable,
      });

      // Update default values based on available options
      const parseConfigOptions = (configValue: string) => {
        if (!configValue) return [];
        try {
          return JSON.parse(configValue);
        } catch {
          return configValue
            .split(',')
            .map((item) => item.trim())
            .filter((item) => item);
        }
      };

      const availableSubproducts = parseConfigOptions(subproductsAvailable);
      const availableInterestCalculations = parseConfigOptions(
        interestCalculationAvailable,
      );

      // Update credit type if current value is not in available options
      if (
        availableSubproducts.length > 0 &&
        !availableSubproducts.includes(creditDetails.type)
      ) {
        setCreditDetails((prev: any) => ({
          ...prev,
          type: availableSubproducts[0] as 'WORKING_CAPITAL' | 'CONSUMER',
        }));
      }

      // Update interest type if current value is not in available options
      if (
        availableInterestCalculations.length > 0 &&
        !availableInterestCalculations.includes(creditDetails.interestType)
      ) {
        setCreditDetails((prev: any) => ({
          ...prev,
          interestType: availableInterestCalculations[0] as
            | 'COMPOUND'
            | 'SIMPLE'
            | 'SIMPLE_FACTORING',
        }));
      }
    } catch (error) {
      console.error(error);
      return configurations;
    }
  };

  const handleChange = (value: string, key: string) => {
    if (key === 'executive') {
      // When changing executive, update both ownerId and executive fields
      setCreditDetails({
        ...creditDetails,
        ownerId: value,
        executive: value,
      });
    } else {
      setCreditDetails({ ...creditDetails, [key]: value });
    }
  };

  const handleChangePeriodicity = (value: string) => {
    let firstPaymentDate = new Date(creditDetails.operationDate);
    const daysToAdd = calculateDateToAdd({ periodicity: value });
    firstPaymentDate.setDate(firstPaymentDate.getDate() + daysToAdd);
    if (workingDayActive === 'active') {
      firstPaymentDate = getDueWorkingDay(firstPaymentDate);
    }
    setCreditDetails({
      ...creditDetails,
      firstPaymentDate: firstPaymentDate.toISOString().split('T')[0],
      periodicity: value,
    });
  };

  const handleOperationDate = (value: string) => {
    const startDate = new Date(value);
    const daysToAdd = calculateDateToAdd({
      periodicity: creditDetails.periodicity,
    });
    let firstPaymentDate = new Date(startDate);
    firstPaymentDate.setDate(startDate.getDate() + daysToAdd);
    if (workingDayActive === 'active') {
      firstPaymentDate = getDueWorkingDay(firstPaymentDate);
    }
    setCreditDetails({
      ...creditDetails,
      operationDate: startDate.toISOString().split('T')[0],
      firstPaymentDate: firstPaymentDate.toISOString().split('T')[0],
    });
  };

  const handleChangeRates = (value: string, key: string) => {
    if (value !== '' && !/^\d*\.?\d*$/.test(value)) return;
    const isIncompleteDecimal = value.endsWith('.') || value === '';

    if (key === 'rate') {
      if (isIncompleteDecimal) {
        setCreditDetails({
          ...creditDetails,
          rate: value,
        });
      } else {
        const numValue = Number(value);
        const anualRate = Number((numValue * 12).toFixed(4));
        const isZero = numValue === 0;
        const isZeroViolated = isZero && !configurations.rateZero;

        setCreditDetails({
          ...creditDetails,
          anualRate,
          rate: value,
          isRateZeroViolated: isZeroViolated,
        });
      }
    } else if (key === 'anualRate') {
      if (isIncompleteDecimal) {
        setCreditDetails({
          ...creditDetails,
          anualRate: value,
        });
      } else {
        const numValue = Number(value);
        const rate = Number((numValue / 12).toFixed(4));
        const isZero = rate === 0;
        const isZeroViolated = isZero && !configurations.rateZero;

        setCreditDetails({
          ...creditDetails,
          rate,
          anualRate: value,
          isRateZeroViolated: isZeroViolated,
        });
      }
    } else if (key === 'arrearRate') {
      if (isIncompleteDecimal) {
        setCreditDetails({
          ...creditDetails,
          arrearRate: value,
        });
      } else {
        const numValue = Number(value);
        const anualArrearRate = Number((numValue * 12).toFixed(4));
        const isArrearRateZero = anualArrearRate === 0;
        const isArrearRateZeroViolated =
          isArrearRateZero && !configurations.arrearRateZero;

        setCreditDetails({
          ...creditDetails,
          anualArrearRate,
          arrearRate: value,
          isArrearRateZeroViolated: isArrearRateZeroViolated,
        });
      }
    } else {
      // anualArrearRate
      if (isIncompleteDecimal) {
        setCreditDetails({
          ...creditDetails,
          anualArrearRate: value,
        });
      } else {
        const numValue = Number(value);
        const arrearRate = Number((numValue / 12).toFixed(4));
        const isArrearRateZero = arrearRate === 0;
        const isArrearRateZeroViolated =
          isArrearRateZero && !configurations.arrearRateZero;

        setCreditDetails({
          ...creditDetails,
          arrearRate,
          anualArrearRate: value,
          isArrearRateZeroViolated: isArrearRateZeroViolated,
        });
      }
    }
  };

  const minFirstPaymentDate = (operationDate: Date) => {
    const firstPaymentDate = new Date(operationDate);
    const daysToAdd = calculateDateToAdd(creditDetails);
    firstPaymentDate.setDate(firstPaymentDate.getDate() + daysToAdd);
    return firstPaymentDate.toISOString().split('T')[0];
  };

  const defaultSubproducts = [
    {
      key: 'WORKING_CAPITAL',
      value: 'Capital de Trabajo',
    },
    {
      key: 'CONSUMER',
      value: 'Consumo',
    },
    {
      key: 'RENEGOTIATION',
      value: 'Renegociación',
    },
  ];

  const defaultInterestCalculations = [
    {
      key: 'COMPOUND',
      value: 'Interés Compuesto',
    },
    {
      key: 'SIMPLE',
      value: 'Interés Simple',
    },
    {
      key: 'SIMPLE_FACTORING',
      value: 'Interés Simple (Factoring)',
    },
  ];

  // Find executive name by ID for display when not editing
  const getExecutiveName = (executiveId: string) => {
    if (!executiveId) return '';

    // Search in portfolio executives first
    const portfolioExecutive = executivesPortfolio?.find((exec: any) => {
      return (
        exec.id === executiveId ||
        exec.id === Number(executiveId) ||
        String(exec.id) === executiveId
      );
    });
    if (portfolioExecutive) {
      return portfolioExecutive.name;
    }

    // Search in all executives
    const allExecutive = executivesAll?.find((exec: any) => {
      return (
        exec.id === executiveId ||
        exec.id === Number(executiveId) ||
        String(exec.id) === executiveId
      );
    });
    if (allExecutive) {
      return allExecutive.name;
    }

    return '';
  };

  return (
    <div>
      <Grid container spacing={1}>
        <Grid item xs={3}>
          <FormControl fullWidth margin="dense" size="small">
            <Select
              value={creditDetails.ownerId || creditDetails.executive || ''}
              disabled={!editing}
              onChange={(e) => handleChange(e.target.value, 'executive')}
              displayEmpty
              renderValue={(value) => {
                // Get the executive ID from creditDetails directly
                const executiveId =
                  creditDetails.ownerId || creditDetails.executive || '';

                if (executiveId) {
                  const name = getExecutiveName(executiveId);
                  return name || 'Seleccionar ejecutivo';
                }
                return 'Seleccionar ejecutivo';
              }}
            >
              <ListSubheader sx={{ fontWeight: 'bold' }}>
                Cliente en cartera ({executivesPortfolio?.length || 0})
              </ListSubheader>
              {executivesPortfolio?.map((executive: any) => (
                <MenuItem key={executive.id} value={executive.id}>
                  {executive.name}
                </MenuItem>
              ))}
              <ListSubheader sx={{ fontWeight: 'bold' }}>
                Todos ({executivesAll?.length || 0})
              </ListSubheader>
              {executivesAll?.map((executive: any) => (
                <MenuItem key={executive.id} value={executive.id}>
                  {executive.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={3}>
          <FormControl fullWidth margin="dense" size="small">
            <InputLabel>Tipo de Crédito</InputLabel>
            <Select
              value={creditDetails.type}
              disabled={!editing}
              onChange={(e) =>
                handleChange(
                  e.target.value as 'WORKING_CAPITAL' | 'CONSUMER',
                  'type',
                )
              }
              label="Tipo de Crédito"
            >
              {availableSubproducts.length > 0
                ? availableSubproducts.map((subproduct: string) => (
                    <MenuItem key={subproduct} value={subproduct}>
                      {subproduct === 'WORKING_CAPITAL'
                        ? 'Capital de Trabajo'
                        : subproduct === 'CONSUMER'
                          ? 'Consumo'
                          : subproduct}
                    </MenuItem>
                  ))
                : defaultSubproducts.map((subproduct) => (
                    <MenuItem key={subproduct.key} value={subproduct.key}>
                      {subproduct.value}
                    </MenuItem>
                  ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={3}>
          <FormControl fullWidth margin="dense" size="small">
            <InputLabel>Método de Cálculo de Interés</InputLabel>
            <Select
              value={creditDetails.interestType}
              disabled={!editing}
              onChange={(e) =>
                handleChange(
                  e.target.value as 'COMPOUND' | 'SIMPLE' | 'SIMPLE_FACTORING',
                  'interestType',
                )
              }
              label="Método de Cálculo de Interés"
            >
              {availableInterestCalculations.length > 0
                ? availableInterestCalculations.map((method: string) => (
                    <MenuItem key={method} value={method}>
                      {method === 'COMPOUND'
                        ? 'Interés Compuesto'
                        : method === 'SIMPLE'
                          ? 'Interés Simple'
                          : method}
                    </MenuItem>
                  ))
                : defaultInterestCalculations.map((method) => (
                    <MenuItem key={method.key} value={method.key}>
                      {method.value}
                    </MenuItem>
                  ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={3}>
          <TextField
            fullWidth
            label="Fecha de Inicio"
            disabled={!editing}
            type="date"
            value={creditDetails.operationDate}
            onChange={(e) => handleOperationDate(e.target.value)}
            margin="dense"
            size="small"
          />
        </Grid>
        <Grid item xs={3}>
          <TextField
            fullWidth
            label="Fecha primer pago"
            type="date"
            disabled={!editing}
            value={creditDetails.firstPaymentDate}
            onChange={(e) => handleChange(e.target.value, 'firstPaymentDate')}
            margin="dense"
            size="small"
            InputProps={{
              inputProps: {
                min: minFirstPaymentDate(new Date(creditDetails.operationDate)),
              },
            }}
          />
        </Grid>

        <Grid item xs={3}>
          <TextField
            fullWidth
            label="Monto Total"
            value={formatMoney(creditDetails.amount)}
            disabled={!editing}
            onChange={(e) => handleChangeMoney(e.target.value, 'amount')}
            margin="dense"
            size="small"
          />
        </Grid>
        <Grid item xs={3}>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Comisión"
                value={formatMoney(creditDetails.operationCost)}
                disabled={!editing}
                onChange={(e) =>
                  handleChangeMoney(e.target.value, 'operationCost')
                }
                margin="dense"
                size="small"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Gastos Operacionales"
                value={formatMoney(creditDetails.otherExpenses)}
                disabled={!editing}
                onChange={(e) =>
                  handleChangeMoney(e.target.value, 'otherExpenses')
                }
                margin="dense"
                size="small"
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={3}>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Notaria"
                value={formatMoney(creditDetails.notary)}
                disabled={!editing}
                onChange={(e) => handleChangeMoney(e.target.value, 'notary')}
                margin="dense"
                size="small"
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Impuestos"
                value={formatMoney(creditDetails.taxes)}
                disabled={!editing}
                onChange={(e) => handleChangeMoney(e.target.value, 'taxes')}
                margin="dense"
                size="small"
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={3}>
          <Grid container spacing={1}>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Cuotas"
                value={creditDetails.months}
                onChange={(e) => handleChange(e.target.value, 'months')}
                disabled={!editing}
                margin="dense"
                size="small"
              />
            </Grid>
            <Grid item xs={8}>
              <FormControl fullWidth margin="dense" size="small">
                <InputLabel>Días entre cuotas</InputLabel>
                <Select
                  value={creditDetails.periodicity}
                  onChange={(e) => handleChangePeriodicity(e.target.value)}
                  disabled={!editing}
                  label="Días entre cuotas"
                >
                  <MenuItem value="WEEKLY">Semanal</MenuItem>
                  <MenuItem value="BIWEEKLY">Quincenal</MenuItem>
                  <MenuItem value="MONTHLY">Mensual</MenuItem>
                  <MenuItem value="BIMONTHLY">Bimestral</MenuItem>
                  <MenuItem value="QUARTERLY">Trimestral</MenuItem>
                  <MenuItem value="SEMI_ANNUAL">Semestral</MenuItem>
                  <MenuItem value="ANNUAL">Anual</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={3}>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Interés Mensual (%)"
                value={creditDetails.rate}
                onChange={(e) => handleChangeRates(e.target.value, 'rate')}
                disabled={!editing}
                margin="dense"
                size="small"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Interés Anual (%)"
                value={creditDetails.anualRate}
                onChange={(e) => handleChangeRates(e.target.value, 'anualRate')}
                disabled={!editing}
                margin="dense"
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={3}>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Mora Mensual (%)"
                value={creditDetails.arrearRate}
                onChange={(e) =>
                  handleChangeRates(e.target.value, 'arrearRate')
                }
                disabled={!editing}
                margin="dense"
                size="small"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Mora Anual (%)"
                value={creditDetails.anualArrearRate}
                onChange={(e) =>
                  handleChangeRates(e.target.value, 'anualArrearRate')
                }
                disabled={!editing}
                margin="dense"
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={3}>
          <FormControl fullWidth margin="dense" size="small">
            <InputLabel>Día Hábil</InputLabel>
            <Select
              value={workingDayActive}
              onChange={(e) =>
                setWorkingDayActive(e.target.value as 'active' | 'inactive')
              }
              disabled={!editing}
              label="Día Hábil"
            >
              <MenuItem value="active">Activar</MenuItem>
              <MenuItem value="inactive">Desactivado</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={3}>
          <Grid container spacing={1}>
            {/* <Grid item xs={12}>
              <WarrantyPopper
                index={0}
                creditRequest={creditDetails}
                setCreditRequest={setCreditDetails}
                canEdit={editing}
              />
            </Grid> */}
            <Grid item xs={12}>
              <GuaranteeObservationPopper
                disabled={!editing}
                formData={creditDetails}
                handleChangeMoney={handleChangeMoney}
                handleChange={handleChange}
                onEditing={editing}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={3}>
          <div style={{ marginTop: '8px' }}>
            <DocumentsSuggestedContainer
              businessIdentifier={selectedRequest?.businessIdentifier}
              setAmountToApply={handleAmountToApply}
              amountToApply={creditDetails.amountToApply}
              maxAmount={maxAmount}
              selected={selectedDocuments}
              setSelected={setSelectedDocuments}
              documents={documents}
              setDocuments={setDocuments}
              setDocumentsToApply={setDocumentsToApply || (() => {})}
              addIconPlacement="end"
              disabled={!editing}
              documentsToApply={creditDetails.documentsToApply || []}
              operationDate={operationDate}
              smallTextField
            />
          </div>
        </Grid>
      </Grid>
    </div>
  );
};

export default Params;
